/**
 * useModalKeys — keyboard dismiss/submit for a BESPOKE modal that rolls its own
 * overlay markup instead of using the shared <AppDialog> (CLAUDE.md standing rule
 * 14, ADR-0013).
 *
 * <AppDialog> already gives Esc / Enter / Ctrl+S; this brings the same to the older
 * hand-rolled popups WITHOUT converting them to `aria-modal` — deliberately, because
 * these popups host toolbars whose shortcuts flow through the global hotkey store,
 * and the store yields entirely to an `[aria-modal]` (so flipping them would silence
 * those shortcuts). Instead the handler lives on the popup's OWN root element:
 *
 *   const modalKeys = useModalKeys(open, { onClose, onSubmit });
 *   return <div className="acc-dialog" {...modalKeys}> … </div>;
 *
 * Why this is safe (same mechanics <AppDialog> and the store already use):
 *  • It is a React `onKeyDown` on the popup root, so it fires AFTER any deeper
 *    handler in the popup (a combo / sub-picker) — and it bails when that inner
 *    handler already consumed the key (`e.defaultPrevented`). So Esc closes the
 *    INNERMOST open thing first, never the wrong layer.
 *  • It runs BEFORE the global store's `document` keydown listener, and calls
 *    `preventDefault`, so the store bails (`hotkeyStore` line 163) — the form BEHIND
 *    the popup never sees the Esc, so its Cancel/Back can't fire and discard edits.
 *  • It auto-focuses the popup root on open (only when focus isn't already inside),
 *    so the keys reach the handler even for popups that don't focus a field.
 *  • Enter submits only when focus is NOT in a textarea / select / button / link /
 *    contenteditable — so it never fires mid-typing.
 */
import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface ModalKeysOptions {
  /** Esc (and a backdrop ✕) closes the popup. */
  onClose?: () => void;
  /** Enter (outside a text field) and Ctrl/⌘+S activate the primary/save action. */
  onSubmit?: () => void;
}

export interface ModalKeysBinding {
  ref: (el: HTMLElement | null) => void;
  onKeyDown: (e: ReactKeyboardEvent) => void;
  tabIndex: number;
}

export function useModalKeys(open: boolean, opts: ModalKeysOptions): ModalKeysBinding {
  const { onClose, onSubmit } = opts;
  const rootRef = useRef<HTMLElement | null>(null);

  // Focus the popup root on open so its onKeyDown receives keys — but never steal
  // focus from a control the popup already focused inside itself.
  useEffect(() => {
    if (!open) return;
    const el = rootRef.current;
    if (el && !el.contains(document.activeElement)) {
      try { el.focus({ preventScroll: true }); } catch { /* not focusable in some envs */ }
    }
  }, [open]);

  const ref = useCallback((el: HTMLElement | null) => { rootRef.current = el; }, []);

  const onKeyDown = useCallback((e: ReactKeyboardEvent) => {
    if (e.defaultPrevented || e.nativeEvent.isComposing) return;   // a nested popup/combo already handled it (or IME composing)
    const key = e.key;

    if (key === 'Escape' && onClose && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault(); e.stopPropagation();
      onClose();
      return;
    }
    if (!onSubmit) return;

    // Ctrl/⌘+S — always submits (blocks the browser Save-page dialog too).
    if ((key === 's' || key === 'S') && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
      e.preventDefault(); e.stopPropagation();
      onSubmit();
      return;
    }
    // Enter — submit only when NOT typing in a field / on a button or link.
    if (key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'A') return;
      if (t?.isContentEditable) return;
      e.preventDefault(); e.stopPropagation();
      onSubmit();
    }
  }, [onClose, onSubmit]);

  return { ref, onKeyDown, tabIndex: -1 };
}
